import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { AiResult } from '../../../../../models/AiResult'
import { selectAiResultScoreLoading } from '../../../../../store/meetingSummary/aiResults'
import { useScoreAiResult } from '../hooks/api'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../tracking'
import { AiFeature } from '../../../../../models/AiFeature'
import './FeedbackModal.scss'

export interface FeedbackModalProps {
  aiFeature: AiFeature
  aiResult: AiResult
  show: boolean
  onHide: () => void
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ aiFeature, aiResult, show, onHide }) => {
  const { tracked } = useTracking()

  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const isResultScoreLoading = useAppSelector(selectAiResultScoreLoading)

  const scoreAiResult = useScoreAiResult()

  const [activeScore, setActiveScore] = useState<number>()

  useEffect(() => {
    const { scores } = aiResult
    const userScore = scores.find(({ userId }) => userId === currentUserId)
    if (userScore) {
      setActiveScore(userScore.score)
    }
  }, [aiResult, currentUserId])

  const feedbackScoreMapping: { [label: string]: number } = useMemo(
    () => ({
      '1': 1,
      '2': 3,
      '3': 5,
      '4': 8,
      '5': 10,
    }),
    [],
  )

  const feedbackModalBody = useMemo(() => {
    return (
      <div className='d-flex flex-column zocks-gap-2'>
        <h4 className='text-center'>Give us feedback</h4>
        <div className='d-flex flex-column gap-2 mb-4'>
          <div className='paragraph-medium'>How would you rate your experience?</div>
          <div className='d-flex gap-2 feedback-score-container'>
            {Object.entries(feedbackScoreMapping).map(([label, score]) => {
              return (
                <Button
                  key={score}
                  variant='header'
                  className={classNames('py-2 zocks-px-0.75', { 'zocks-bg-color-primary zocks-text-white': activeScore === score })}
                  onClick={() => setActiveScore(score)}
                  disabled={isResultScoreLoading}
                >
                  {label}
                </Button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }, [activeScore, feedbackScoreMapping, isResultScoreLoading])

  const _handleSaveScore = async () => {
    if (!activeScore) {
      return
    }
    const { id: aiResultId } = aiResult
    await scoreAiResult(aiResultId, activeScore)
    onHide()
  }

  const handleSaveScore = tracked(_handleSaveScore, {
    eventName: TrackEvent.AI_RESULT_COPIED,
    eventDetails: {
      ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
      score: activeScore,
    },
  })

  return (
    <Modal
      header={<></>}
      body={feedbackModalBody}
      show={show}
      onHide={onHide}
      showFooterActions
      footerPrimaryActionTitle={'Send'}
      footerPrimaryActionLoading={isResultScoreLoading}
      footerPrimaryActionDisabled={!activeScore}
      onFooterPrimaryAction={handleSaveScore}
      footerClassName={'feedback-modal-footer'}
    />
  )
}
